/* eslint-disable @wordpress/no-unsafe-wp-apis */
/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import {
  InspectorControls,
} from '@wordpress/block-editor';
import {
	PanelBody,
	TextControl,
	Button,
	__experimentalElevation as Elevation,
} from '@wordpress/components';
import {
	Fragment, useContext
} from '@wordpress/element';
import {applyFilters} from '@wordpress/hooks';
import { decodeEntities } from '@wordpress/html-entities';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import 'react-datepicker/dist/react-datepicker.css';
import BlockContext from './context/blockContext.js';
/**
 * Internal dependencies
 */

import * as Controls from '../../controls';

import './editor.scss';
import SelectStyle from '../../options/selected-style.js';
import signatureImageUrl from '../../../includes/assets/img/sign-white.png';

// All Controls Import here
import AboutControl from './InspectorControls/About.js';
import ContactControl from './InspectorControls/Contact.js';
import ProfileControl from './InspectorControls/Profile.js';
import SkillsControl from './InspectorControls/Skills.js';
import LanguagesControl from './InspectorControls/Languages.js';
import HobbiesControl from './InspectorControls/Hobbies.js';
import EducationControl from './InspectorControls/Education.js';
import ExperienceControl from './InspectorControls/Experience.js';
import ReferenceControl from './InspectorControls/Reference.js';
import AwardControl from './InspectorControls/Award.js';
import ToolsControl from './InspectorControls/Tools.js';
import AddSection from './InspectorControls/AddSection.js';
import FooterControl from './InspectorControls/Footer.js';

const Inspector = () => {
	const {attributes, setAttributes} = useContext(BlockContext);
  const {
    style,
    footerPanel,
    isFooterVisible,
    footerLink,
    signature,
    footerBtnText,
    footerBtnLink,
    sectionItems,
    sectionItems21,
    sectionItems26,
    sectionItems27,
    sectionItems28,
    themeColor,
    typography,
	aboutTitleColor,
	aboutTitleBgColor,
	aboutDescColor,
	deletedSectionsData,
  } = attributes;
  const getSectionItems = () => {
    switch (style) {
      case 'style21':
      case 'style23':
        return sectionItems21 || [];
      case 'style26':
        return sectionItems26 || [];
      case 'style27':
        return sectionItems27 || [];
      case 'style28':
        return sectionItems28 || [];
      default:
        return sectionItems || [];
    }
  };

  const setSectionItems = (newItems) => {
    switch (style) {
      case 'style21':
      case 'style23':
        setAttributes({ sectionItems21: newItems });
        break;
      case 'style26':
        setAttributes({ sectionItems26: newItems });
        break;
      case 'style27':
        setAttributes({ sectionItems27: newItems });
        break;
      case 'style28':
        setAttributes({ sectionItems28: newItems });
        break;
		default:
		setAttributes({ sectionItems: newItems });			
    }
  };
	const {
		CustomPublishPreviewButtons,
		ImageSelector,
		ColorControl,
		MyTypographyControl
	} = Controls;

  const handleYearMonChange = (date, sectionIndex, itemIndex, fieldName) => {
    const currentSectionItems = getSectionItems();
    const newSectionItems = [...currentSectionItems];

    if (date && date instanceof Date && !isNaN(date)) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const formattedDate = `${year}-${month}`;
      newSectionItems[sectionIndex].items[itemIndex][fieldName] = formattedDate;
    } else {
      newSectionItems[sectionIndex].items[itemIndex][fieldName] = null;
    }

    setSectionItems(newSectionItems);
  };

  const handleAddItem = (sectionIndex) => {
    const newSectionItems = [...getSectionItems()];
    const section = newSectionItems[sectionIndex];
    const newItem =
      section.type === 'skills'
        ? { name: '', percentage: 0 }
        : section.type === 'hobbies'
          ? { name: '', icon: '' }
          : {};
    newSectionItems[sectionIndex] = {
      ...section,
      items: [...(section.items || []), newItem],
    };
    setSectionItems(newSectionItems);
  };

  const handleRemoveItem = (sectionIndex, itemIndex) => {
    const newSectionItems = [...getSectionItems()];
    const section = newSectionItems[sectionIndex];
    const newItems = [...section.items];
    newItems.splice(itemIndex, 1);
    newSectionItems[sectionIndex] = {
      ...section,
      items: newItems,
    };
    setSectionItems(newSectionItems);
  };

  const handleDuplicateItem = (sectionIndex, itemIndex) => {
    const newSectionItems = [...getSectionItems()];
    const section = newSectionItems[sectionIndex];
    const itemToDuplicate = JSON.parse(
      JSON.stringify(section.items[itemIndex])
    );
    const newItems = [...section.items];
    newItems.splice(itemIndex + 1, 0, itemToDuplicate);
    newSectionItems[sectionIndex] = {
      ...section,
      items: newItems,
    };
    setSectionItems(newSectionItems);
  };

  const handleChangeItemField = (value, sectionIndex, itemIndex, field) => {
    const sectionItems = getSectionItems();
    const updatedSections = sectionItems.map((section, secIdx) => {
      if (secIdx === sectionIndex) {
        return {
          ...section,
          items: section.items.map((item, idx) =>
            idx === itemIndex ? { ...item, [field]: value } : item
          ),
        };
      }
      return section;
    });
    setSectionItems(updatedSections);
  };

  const handleRemoveSection = (sectionIndex) => {
    const currentSections = [...getSectionItems()];
    const sectionToRemove = currentSections[sectionIndex];
    
    // Store the section data AND position before removing it
    const currentDeletedSectionsData = deletedSectionsData || {};
    const updatedDeletedSectionsData = {
        ...currentDeletedSectionsData,
        [sectionToRemove.type]: {
            sectionData: sectionToRemove,
            position: sectionIndex  // Store the original position
        }
    };
    
    // Update the deleted sections data
    setAttributes({ deletedSectionsData: updatedDeletedSectionsData });
    
    // Remove the section from current sections
    currentSections.splice(sectionIndex, 1);
    setSectionItems(currentSections);
};

  const onSectionDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination, type } = result;
    const sectionItems = getSectionItems();

    if (type === 'section') {
      const updatedSections = Array.from(sectionItems);
      const [movedSection] = updatedSections.splice(source.index, 1);
      updatedSections.splice(destination.index, 0, movedSection);
      setSectionItems(updatedSections);
    } else if (type === 'item') {
      const sectionIndex = parseInt(source.droppableId.replace('items-', ''));
      const updatedSections = [...sectionItems];
      const updatedItems = Array.from(updatedSections[sectionIndex].items);
      const [movedItem] = updatedItems.splice(source.index, 1);
      updatedItems.splice(destination.index, 0, movedItem);
      updatedSections[sectionIndex].items = updatedItems;
      setSectionItems(updatedSections);
    }
  };

  const handleDateChange = (date, sectionIndex) => {
    const formattedDate = date ? date.toISOString().split('T')[0] : null;
    handleChangeSectionField(formattedDate, sectionIndex, 'dateOfBirth');
  };
  const handleYearChange = (date, sectionIndex, itemIndex, fieldName) => {
    const currentSectionItems = [...getSectionItems()];
    currentSectionItems[sectionIndex].items[itemIndex][fieldName] = date
      ? date.toISOString()
      : null;
    setSectionItems(currentSectionItems);
  };

  const handleChangeSectionField = (value, index, field) => {
    const updatedSections = [...getSectionItems()];
    updatedSections[index] = {
      ...updatedSections[index],
      [field]: value,
    };
    setSectionItems(updatedSections);
  };


  const capitalizeTitle = (title, index, titles) => {
    if (typeof title === 'string' && title.trim().length > 0) {
      return title.charAt(0).toUpperCase() + title.slice(1);
    }
    return titles?.[index] || `Section ${index + 1}`;
  };

  // Hooks Declaration
  const showPresetTemplate = applyFilters(
    'bwdcv-preset-template-visibility',
    true
  );
  const dashPreviewBannerInspect = applyFilters(
    'bwdcv-dash-preview-banner-inspect',
    false
  );
  const footerVisibility = applyFilters('bwdcv-footer-visibility', true);
  const truncateTitle = (text, maxLength = 20) => {
    if (!text) return 'New Item';
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  const currentSectionItems = getSectionItems();
  return (
    <InspectorControls>
      {dashPreviewBannerInspect && <CustomPublishPreviewButtons />}
      <Fragment>
        <div className="first-two-container-wrapper">
          {/* Start Preset Template Tab ---------------*/}
          {showPresetTemplate && (
            <PanelBody
              title={__('Preset Template', 'cv-builder')}
              className="bwdcvs-tab-heading"
              initialOpen={false}
            >
              <ImageSelector
                options={SelectStyle}
                value={style}
                onChange={(selectedStyle) =>
                  setAttributes({ style: selectedStyle })
                }
              />
            </PanelBody>
          )}
            <PanelBody
                title={__(
                  'Global Style',
                  'cv-builder'
                )}
                className='bwdcvs-tab-heading'
                initialOpen={false}
              >
                <ColorControl
                  label={__(
                    'Theme Color',
                    'cv-builder'
                  )}
                  color={themeColor}
                  colorName="themeColor"
                  onChange={setAttributes}
                />
                <MyTypographyControl
                  label={__(
                    'Typography',
                    'cv-builder'
                  )}
                  typography={
                    typography
                  }
                  onChange={(value) => {
                    setAttributes({
                      typography:
                        value,
                    });
                  }}
                  attribute={attributes}
                  setAttributes={setAttributes}
                />
            </PanelBody>
          {/* Start Profile Tab ---------------*/}
          <PanelBody
            title={__('Profile', 'cv-builder')}
            className="bwdcvs-tab-heading"
            initialOpen={true}
            icon="admin-users"
          >
            <ProfileControl
              attributes={attributes}
              setAttributes={setAttributes}
            />
          </PanelBody>
        </div>

        <div className="bwdcv-repeater-content-wrapper">
          {currentSectionItems.length > 0 && (
            <DragDropContext onDragEnd={onSectionDragEnd}>
              <Droppable droppableId="sections" type="section">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {currentSectionItems.map((section, index) => (
                      <Draggable
                        key={section.id}
                        draggableId={`section-${section.id}`}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={
                              provided.innerRef
                            }
                            {...provided.draggableProps}
                            className="draggable-section"
                            id={`section${section.id}`}
                          >
                            <div
                              {...provided.dragHandleProps}
                              className="bwdtm-drag-handle"
                            >
                              ☰
                            </div>
                            <PanelBody
                              title={capitalizeTitle(
                                decodeEntities(section?.title),
                                index
                              )}
                              initialOpen={
                                false
                              }
                              className="bwdcvs-tab-heading"
                            >

                              <TextControl
                                label={__('Section Title', 'cv-builder')}
                                value={decodeEntities(section.title || '')}
                                onChange={(value) =>
                                  handleChangeSectionField(
                                    value,
                                    index,
                                    'title'
                                  )
                                }
                              />
                              <hr />

                              {/* Start About Tab ---------------*/}
                              {section.type === 'about' && (
                                <>
                                  <AboutControl
                                    section={section}
                                    index={index}
                                    handleChangeSectionField={handleChangeSectionField}
                                    handleDateChange={handleDateChange}
                                    aboutDescColor={aboutDescColor}
                                    setAttributes={setAttributes}
                                  />
                                </>
                              )}
                              {/* Start Contact Tab ---------------*/}
                              {section.type === 'contact' && (
                                <>
                                  <ContactControl
                                    section={section}
                                    index={index}
                                    getSectionItems={getSectionItems}
                                    setSectionItems={setSectionItems}
                                    handleChangeSectionField={handleChangeSectionField}
                                  />

                                </>
                              )}
                              {/* Start Section Inside Repeater  ---------------*/}
                              {section.items && (
                                <Droppable
                                  droppableId={`items-${index}`}
                                  type="item"
                                >
                                  {(
                                    provided
                                  ) => (
                                    <div
                                      {...provided.droppableProps}
                                      ref={
                                        provided.innerRef
                                      }
                                      className="bwdcv-repeater-cluster"
                                    >
                                      {Array.isArray(
                                        section.items
                                      ) &&
                                        section.items.map(
                                          (
                                            item,
                                            itemIndex
                                          ) => (
                                            <Draggable
                                              key={`item-${itemIndex}`}
                                              draggableId={`item-${itemIndex}`}
                                              index={
                                                itemIndex
                                              }
                                            >
                                              {(
                                                provided
                                              ) => (
                                                <div
                                                  ref={
                                                    provided.innerRef
                                                  }
                                                  {...provided.draggableProps}
                                                  className="inner-repeater-wrap"
                                                >
                                                  <div className="bwdcv-repeater-wrapper">
                                                    <PanelBody
                                                      key={
                                                        itemIndex
                                                      }
                                                      title={truncateTitle(item.name, 20)}
                                                      initialOpen={
                                                        false
                                                      }
                                                      className="bwdcvs-tab-heading"
                                                    >

                                                      <div className="item-fields">
                                                        {/* Start Skills Tab ---------------*/}
                                                        {section.type === 'skills' && (
                                                          <>
                                                            <SkillsControl
                                                              section={section}
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={
                                                                handleChangeItemField
                                                              }
                                                            />
                                                          </>
                                                        )}
                                                        {/* Start Languages Tab ---------------*/}
                                                        {section.type === 'languages' && (
                                                          <>
                                                            <LanguagesControl
                                                              section={section}
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={handleChangeItemField}
                                                            />
                                                          </>
                                                        )}
                                                        {/* Start Hobbies Tab ---------------*/}
                                                        {section.type === 'hobbies' && (
                                                          <>
                                                            <HobbiesControl
                                                              section={section}
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={handleChangeItemField}
                                                            />
                                                          </>
                                                        )}
                                                        {/* Start Education Tab ---------------*/}
                                                        {section.type === 'education' && (
                                                          <>
                                                            <EducationControl
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={handleChangeItemField}
                                                              handleYearChange={handleYearChange}
                                                              handleYearMonChange={handleYearMonChange}
                                                            />
                                                          </>
                                                        )}
                                                        {/* Start Experience Tab ---------------*/}
                                                        {section.type === 'experience' && (
                                                          <>
                                                            <ExperienceControl
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              sectionItems={getSectionItems()}
                                                              setAttributes={setAttributes}
                                                              handleChangeItemField={handleChangeItemField}
                                                              handleYearMonChange={handleYearMonChange}
                                                            />

                                                          </>
                                                        )}
                                                        {/* Start Reference Tab ---------------*/}
                                                        {section.type === 'reference' && (
                                                          <>
                                                            <ReferenceControl
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={handleChangeItemField}
                                                            />
                                                          </>
                                                        )}
                                                        {/* Start Award Tab ---------------*/}
                                                        {section.type === 'awards' && (
                                                          <>
                                                            <AwardControl
                                                              section={section}
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={
                                                                handleChangeItemField
                                                              }
                                                            />
                                                          </>
                                                        )}
                                                        {/* Start Tools Tab ---------------*/}
                                                        {section.type === 'tools' && (
                                                          <>
                                                            <ToolsControl
                                                              section={section}
                                                              index={index}
                                                              item={item}
                                                              itemIndex={itemIndex}
                                                              handleChangeItemField={
                                                                handleChangeItemField
                                                              }
                                                            />
                                                          </>
                                                        )}
                                                      </div>
                                                    </PanelBody>
                                                    {section.type !== 'about' && section.type !== 'contact' && (
                                                      <>
                                                        <div
                                                          {...provided.dragHandleProps}
                                                          className="bwdtm-drag-handle"
                                                        >
                                                          ☰
                                                        </div>
                                                        <Button
                                                          onClick={() =>
                                                            handleDuplicateItem(
                                                              index,
                                                              itemIndex
                                                            )
                                                          }
                                                          className="duplicate-item-button"
                                                          label={__(
                                                            'Duplicate Item',
                                                            'cv-builder'
                                                          )}
                                                        >
                                                          {' '}
                                                          <svg
                                                            width="20"
                                                            height="20"
                                                            viewBox="0 0 448 512"
                                                          >
                                                            <path d="M384 336H192c-8.8 0-16-7.2-16-16V64c0-8.8 7.2-16 16-16l140.1 0L400 115.9V320c0 8.8-7.2 16-16 16zM192 384H384c35.3 0 64-28.7 64-64V115.9c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1H192c-35.3 0-64 28.7-64 64V320c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64V448c0 35.3 28.7 64 64 64H256c35.3 0 64-28.7 64-64V416H272v32c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16V192c0-8.8 7.2-16 16-16H96V128H64z" />
                                                          </svg>
                                                        </Button>

                                                        <Button
                                                          isDestructive
                                                          onClick={() =>
                                                            handleRemoveItem(
                                                              index,
                                                              itemIndex
                                                            )
                                                          }
                                                          className="delete-item-button"
                                                        >
                                                          <span className="dashicons dashicons-trash"></span>
                                                        </Button>
                                                      </>
                                                    )}
                                                  </div>
                                                </div>
                                              )}
                                            </Draggable>
                                          )
                                        )}
                                      {
                                        provided.placeholder
                                      }
                                      {section.type !== 'about' && section.type !== 'contact' && (
                                        <div className="bwdcv-add-btn-wrap">
                                          <button
                                            onClick={() =>
                                              handleAddItem(
                                                index
                                              )
                                            }
                                            className="add-item-button"
                                          >
                                            {__(
                                              'Add New Item',
                                              'cv-builder'
                                            )}
                                            <Elevation
                                              active={
                                                1
                                              }
                                              focus={
                                                5
                                              }
                                              hover={
                                                7
                                              }
                                              value={
                                                5
                                              }
                                            />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </Droppable>
                              )}
                            </PanelBody>
                            <Button
                              isDestructive
                              onClick={() =>
                                handleRemoveSection(
                                  index
                                )
                              }
                              className="trash-icon"
                            >
                              <span className="dashicons dashicons-trash"></span>
                            </Button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
        {
          footerVisibility && (
            <FooterControl
              setAttributes={setAttributes}
              footerPanel={footerPanel}
              isFooterVisible={isFooterVisible}
              signature={signature}
              footerLink={footerLink}
              footerBtnText={footerBtnText}
              footerBtnLink={footerBtnLink}
              signatureImageUrl={signatureImageUrl}
        />
          )
        }
        <AddSection
          setAttributes={setAttributes}
          getSectionItems={getSectionItems}
          setSectionItems={setSectionItems}
        />

      </Fragment>
    </InspectorControls>
  );
};

export default Inspector;
